import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import {
  BookOpenIcon,
  ClockIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  ArrowLeftIcon,
  HomeIcon,
  SparklesIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import SearchBar from './SearchBar';
import HeaderNav from '../HeaderNav';
import './Learning.css';

const PAGE_MOTION = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: 'easeOut' },
};

const LearningHub = ({ userId, user, onLogout }) => {
  const [categories, setCategories] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [gameUnlocks, setGameUnlocks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [userId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, statsRes, unlocksRes] = await Promise.all([
        axios.get('/api/learning/categories').catch(() => ({ data: [] })),
        userId ? axios.get(`/api/learning/stats/${userId}`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        axios.get('/api/game/city-of-signals/unlocks').catch(() => ({ data: null })),
      ]);

      setCategories(categoriesRes.data || []);
      setUserStats(statsRes.data);
      setGameUnlocks(unlocksRes.data);
    } catch (error) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  const getCategoryProgress = () => 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <p className="text-body text-secondary">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      {user && (
        <HeaderNav
          activeSection="learn-and-play"
          onSectionChange={() => {}}
          user={user}
          onLogout={onLogout}
          visionCount={0}
        />
      )}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-secondary hover:text-primary mb-8 transition-colors duration-300"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <HomeIcon className="w-5 h-5" />
          <span className="text-body">Back to dashboard</span>
        </button>

        <div className="mb-10">
          <motion.div {...PAGE_MOTION} className="flex items-center justify-between gap-4">
            <div>
              <h1 className="font-display text-h1 text-primary mb-2">Learning hub</h1>
              <p className="text-body text-secondary">
                Master financial concepts through interactive roadmaps
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowSearch(!showSearch)}
              className="btn-secondary p-3"
              aria-label="Search"
            >
              <MagnifyingGlassIcon className="w-5 h-5" />
            </button>
          </motion.div>

          {showSearch && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="mt-6"
            >
              <SearchBar onClose={() => setShowSearch(false)} />
            </motion.div>
          )}
        </div>

        {userStats && (
          <motion.section
            {...PAGE_MOTION}
            transition={{ ...PAGE_MOTION.transition, delay: 0.05 }}
            className="surface-panel p-5 md:p-6 mb-10"
          >
            <h2 className="font-display text-h2 text-primary mb-5">Your progress</h2>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-6 hairline-list md:border-0">
              {[
                { label: 'Completed', value: userStats.total_completed, icon: CheckCircleIcon },
                {
                  label: 'Time spent',
                  value: `${Math.floor(userStats.total_time_spent / 60)}m`,
                  icon: ClockIcon,
                },
                {
                  label: 'Consistency',
                  value: `${userStats.streak_days || 0} days`,
                  icon: BookOpenIcon,
                },
                { label: 'Badges', value: userStats.badges?.length || 0, icon: CheckCircleIcon },
              ].map(({ label, value, icon: Icon }) => (
                <div key={label} className="py-3 md:py-0 md:border-0 border-t border-hairline first:border-t-0">
                  <dt className="flex items-center gap-2 text-label text-secondary mb-1">
                    <Icon className="w-4 h-4 text-faint" />
                    {label}
                  </dt>
                  <dd className="figure font-display text-h2 text-primary tabular-nums">{value}</dd>
                </div>
              ))}
            </dl>
          </motion.section>
        )}

        <motion.div {...PAGE_MOTION} transition={{ ...PAGE_MOTION.transition, delay: 0.1 }} className="mb-10">
          <Link
            to="/game/city-of-signals"
            className="block surface-panel p-6 hover:bg-pine-tint/30 transition-colors duration-300 group"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg border border-hairline bg-pine-tint flex items-center justify-center">
                  <SparklesIcon className="w-6 h-6 text-pine" />
                </div>
                <div>
                  <h2 className="font-display text-h2 text-primary mb-1">Play Aura City of Lights</h2>
                  <p className="text-label text-secondary">
                    {gameUnlocks?.learning
                      ? `Your progress: ${gameUnlocks.learning.completed_count} of ${gameUnlocks.learning.total_paths} learning paths complete.`
                      : 'Progress in learning unlocks more game levels. Level 1 is ready to play.'}
                  </p>
                </div>
              </div>
              <span className="text-label text-pine font-medium flex items-center gap-1">
                Play game <ArrowRightIcon className="w-4 h-4" />
              </span>
            </div>
          </Link>
        </motion.div>

        {userStats && userStats.categories_in_progress > 0 && (
          <motion.section
            {...PAGE_MOTION}
            transition={{ ...PAGE_MOTION.transition, delay: 0.12 }}
            className="surface-panel p-6 mb-10"
          >
            <h2 className="font-display text-h2 text-primary mb-2">Continue learning</h2>
            <p className="text-body text-secondary">
              You have {userStats.categories_in_progress} categories in progress
            </p>
          </motion.section>
        )}

        <div className="mb-10">
          <h2 className="font-display text-h2 text-primary mb-6">Explore topics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category, index) => {
              const progress = getCategoryProgress(category.id);
              return (
                <motion.button
                  key={category.id}
                  type="button"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: 'easeOut', delay: index * 0.05 }}
                  className="surface-panel p-6 text-left hover:bg-pine-tint/30 transition-colors duration-300 group w-full"
                  onClick={() => navigate(`/learning/${category.id}`)}
                >
                  <div className="w-10 h-10 rounded-lg border border-hairline bg-bone flex items-center justify-center mb-4 text-xl">
                    {category.icon}
                  </div>
                  <h3 className="font-display text-h3 text-primary mb-2 group-hover:text-pine transition-colors duration-300">
                    {category.name}
                  </h3>
                  <p className="text-label text-secondary mb-4 line-clamp-2">{category.description}</p>
                  <div className="flex items-center justify-between text-label text-faint">
                    <span className="flex items-center gap-1">
                      <BookOpenIcon className="w-4 h-4" />
                      {category.node_count} lessons
                    </span>
                    <span className="flex items-center gap-1">
                      <ClockIcon className="w-4 h-4" />
                      {category.estimated_time}
                    </span>
                  </div>
                  {userId && progress > 0 && (
                    <div className="mt-4">
                      <div className="flex justify-between text-label text-secondary mb-1">
                        <span>Progress</span>
                        <span className="figure tabular-nums">{progress}%</span>
                      </div>
                      <div className="progress-bar-track">
                        <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
                      </div>
                    </div>
                  )}
                  {category.node_count === 0 && (
                    <span className="badge-mark mt-4 inline-block">Coming soon</span>
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>

        <motion.section
          {...PAGE_MOTION}
          transition={{ ...PAGE_MOTION.transition, delay: 0.2 }}
          className="surface-panel p-8 text-center"
        >
          <h3 className="font-display text-h2 text-primary mb-4">Designed for your learning style</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-label text-secondary">
            {[
              'Visual articles with infographics',
              'Short, engaging video lessons',
              'Interactive simulations & tools',
              'Quizzes to test your knowledge',
            ].map((text) => (
              <p key={text}>{text}</p>
            ))}
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default LearningHub;
